// Minimal, dependency-free encoder for an uncompressed baseline TIFF from 8-bit pixel data
// (grayscale, RGB, or RGBA).
//
// Used instead of JPEG/PNG when handing an assembled preview texture to the local render engine:
// JPEG-encoding a large canvas (e.g. 7000x6000) can take over a second, while this is close to a
// memcpy. The engine already reads uncompressed TIFF for the same purpose on the backend render
// task path (see render-with-tiles-backend.ts), via OpenImageIO - no engine-side changes needed.
const TIFF_TAG_IMAGE_WIDTH = 256
const TIFF_TAG_IMAGE_LENGTH = 257
const TIFF_TAG_BITS_PER_SAMPLE = 258
const TIFF_TAG_COMPRESSION = 259
const TIFF_TAG_PHOTOMETRIC_INTERPRETATION = 262
const TIFF_TAG_STRIP_OFFSETS = 273
const TIFF_TAG_SAMPLES_PER_PIXEL = 277
const TIFF_TAG_ROWS_PER_STRIP = 278
const TIFF_TAG_STRIP_BYTE_COUNTS = 279
const TIFF_TAG_EXTRA_SAMPLES = 338

const TIFF_TYPE_SHORT = 3
const TIFF_TYPE_LONG = 4

const PHOTOMETRIC_BLACK_IS_ZERO = 1
const PHOTOMETRIC_RGB = 2

const FILE_HEADER_SIZE = 8 // byte-order mark + magic number + offset to first IFD
const BITS_PER_SAMPLE_ENTRY_INDEX = 2 // index of the BitsPerSample entry within `entries` below

type IfdEntry = {tag: number; type: number; count: number; value: number}

// pixels must be top-to-bottom, `channels` bytes/pixel (grayscale, RGB, or RGBA), width*height*channels
// bytes - exactly what canvas ImageData.data already is for channels=4, so callers can pass it straight
// through with no reordering or flipping; for channels=3, strip the alpha byte per pixel first; for
// channels=1, extract a single component per pixel first.
export function encodeUncompressedTiff(pixels: Uint8ClampedArray | Uint8Array, width: number, height: number, channels: 1 | 3 | 4): Uint8Array {
    const pixelDataLength = width * height * channels
    const pixelDataOffset = FILE_HEADER_SIZE
    const hasAlpha = channels === 4
    const photometricInterpretation = channels === 1 ? PHOTOMETRIC_BLACK_IS_ZERO : PHOTOMETRIC_RGB

    // Per the TIFF6.0 spec, an IFD entry's 4-byte value/offset field holds the value(s) inline when they
    // fit (total size <= 4 bytes) and is only a pointer to an external array otherwise. BitsPerSample is
    // SHORT (2 bytes) x channels: for channels=1 that's 2 bytes, which fits inline - treating it as an
    // offset in that case (as an earlier version of this code unconditionally did) writes a nonsensical
    // "BitsPerSample" value (the offset itself, e.g. in the millions) for any spec-compliant reader,
    // corrupting single-channel (e.g. roughness) atlases specifically - multi-channel images happened to
    // never hit this because their BitsPerSample arrays are large enough to genuinely need the offset form.
    const bitsPerSampleFitsInline = channels * 2 <= 4

    const entries: IfdEntry[] = [
        {tag: TIFF_TAG_IMAGE_WIDTH, type: TIFF_TYPE_LONG, count: 1, value: width},
        {tag: TIFF_TAG_IMAGE_LENGTH, type: TIFF_TYPE_LONG, count: 1, value: height},
        // inline case: store the actual BitsPerSample value (8) directly; offset case: patched below once
        // its offset is known.
        {tag: TIFF_TAG_BITS_PER_SAMPLE, type: TIFF_TYPE_SHORT, count: channels, value: bitsPerSampleFitsInline ? 8 : 0},
        {tag: TIFF_TAG_COMPRESSION, type: TIFF_TYPE_SHORT, count: 1, value: 1}, // 1 = no compression
        {tag: TIFF_TAG_PHOTOMETRIC_INTERPRETATION, type: TIFF_TYPE_SHORT, count: 1, value: photometricInterpretation},
        {tag: TIFF_TAG_STRIP_OFFSETS, type: TIFF_TYPE_LONG, count: 1, value: pixelDataOffset},
        {tag: TIFF_TAG_SAMPLES_PER_PIXEL, type: TIFF_TYPE_SHORT, count: 1, value: channels},
        {tag: TIFF_TAG_ROWS_PER_STRIP, type: TIFF_TYPE_LONG, count: 1, value: height}, // everything in a single strip
        {tag: TIFF_TAG_STRIP_BYTE_COUNTS, type: TIFF_TYPE_LONG, count: 1, value: pixelDataLength},
        ...(hasAlpha
            ? [{tag: TIFF_TAG_EXTRA_SAMPLES, type: TIFF_TYPE_SHORT, count: 1, value: 2} satisfies IfdEntry] // 2 = unassociated alpha (source alpha is always opaque anyway)
            : []),
    ]
    // IFD entries must be tag-ordered ascending per the TIFF spec - already true above (EXTRA_SAMPLES, when present, has the highest tag).

    const ifdOffset = pixelDataOffset + pixelDataLength
    const ifdSize = 2 + entries.length * 12 + 4 // entry count + entries + next-IFD offset
    const bitsPerSampleOffset = ifdOffset + ifdSize
    if (!bitsPerSampleFitsInline) {
        entries[BITS_PER_SAMPLE_ENTRY_INDEX].value = bitsPerSampleOffset
    }

    const bitsPerSampleArraySize = bitsPerSampleFitsInline ? 0 : channels * 2 // one SHORT per channel - too big to inline in an IFD entry
    const totalSize = bitsPerSampleOffset + bitsPerSampleArraySize
    const buffer = new ArrayBuffer(totalSize)
    const view = new DataView(buffer)
    const bytes = new Uint8Array(buffer)

    // Header: "II" = little-endian byte order, then the TIFF magic number, then the IFD offset
    bytes[0] = 0x49
    bytes[1] = 0x49
    view.setUint16(2, 42, true)
    view.setUint32(4, ifdOffset, true)

    // Pixel data: verbatim copy - already top-to-bottom in the target channel layout, matching TIFF's
    // default row order, so no reordering is needed.
    bytes.set(pixels, pixelDataOffset)

    // IFD
    view.setUint16(ifdOffset, entries.length, true)
    entries.forEach((entry, i) => {
        const entryOffset = ifdOffset + 2 + i * 12
        view.setUint16(entryOffset, entry.tag, true)
        view.setUint16(entryOffset + 2, entry.type, true)
        view.setUint32(entryOffset + 4, entry.count, true)
        view.setUint32(entryOffset + 8, entry.value, true)
    })
    view.setUint32(ifdOffset + 2 + entries.length * 12, 0, true) // no next IFD

    // BitsPerSample array (referenced by offset above since it doesn't fit inline) - only emitted when
    // channels > 1; for channels=1 the value was already written inline into the IFD entry itself above.
    if (!bitsPerSampleFitsInline) {
        for (let i = 0; i < channels; i++) {
            view.setUint16(bitsPerSampleOffset + i * 2, 8, true)
        }
    }

    return bytes
}
